import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid, Paper, Typography, Box, Chip, Button, IconButton, Tooltip,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Snackbar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Collapse, Divider,
} from '@mui/material';
import {
  Cloud as CloudIcon, Storage as DatabaseIcon, RestartAlt as RestartIcon,
  RocketLaunch as RedeployIcon, CheckCircle as RunningIcon,
  Cancel as StoppedIcon, Sync as DeployingIcon, Warning as WarningIcon,
  Error as ErrorIcon, ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import railwayApi from '../../services/railwayApi';

const POLL_INTERVAL = 90000;

function RailwayMonitoringPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serviceLogs, setServiceLogs] = useState({});
  const [expandedLogs, setExpandedLogs] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, serviceId: null, serviceName: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const result = await railwayApi.getStatus();
      setData(result);
    } catch (err) {
      console.error('Error fetching Railway status:', err);
      setError(err.response?.data?.message || 'Failed to fetch Railway status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleFetchLogs = async (serviceId) => {
    if (expandedLogs[serviceId]) {
      setExpandedLogs(prev => ({ ...prev, [serviceId]: false }));
      return;
    }
    try {
      const result = await railwayApi.getServiceLogs(serviceId);
      setServiceLogs(prev => ({ ...prev, [serviceId]: result.data }));
      setExpandedLogs(prev => ({ ...prev, [serviceId]: true }));
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to fetch logs', severity: 'error' });
    }
  };

  const handleAction = (action, serviceId, serviceName) => {
    setConfirmDialog({ open: true, action, serviceId, serviceName });
  };

  const handleConfirmAction = async () => {
    const { action, serviceId, serviceName } = confirmDialog;
    setConfirmDialog({ open: false, action: null, serviceId: null, serviceName: '' });
    setActionLoading(prev => ({ ...prev, [`${action}-${serviceId}`]: true }));
    try {
      if (action === 'restart') {
        await railwayApi.restartService(serviceId);
        setSnackbar({ open: true, message: `${serviceName} restart initiated`, severity: 'success' });
      } else if (action === 'redeploy') {
        await railwayApi.redeployService(serviceId);
        setSnackbar({ open: true, message: `${serviceName} redeployment initiated`, severity: 'success' });
      }
      setTimeout(fetchData, 3000);
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to ${action} ${serviceName}: ${err.response?.data?.message || err.message}`, severity: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [`${action}-${serviceId}`]: false }));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'RUNNING': return <RunningIcon sx={{ color: 'success.main' }} />;
      case 'STOPPED': return <StoppedIcon sx={{ color: 'error.main' }} />;
      case 'DEPLOYING': return <DeployingIcon sx={{ color: 'info.main' }} />;
      case 'ERROR': return <ErrorIcon sx={{ color: 'error.main' }} />;
      default: return <WarningIcon sx={{ color: 'warning.main' }} />;
    }
  };

  const getStatusLabel = (status) => {
    const map = {
      RUNNING: { label: 'Running', color: 'success' },
      STOPPED: { label: 'Stopped', color: 'error' },
      DEPLOYING: { label: 'Deploying', color: 'info' },
      ERROR: { label: 'Error', color: 'error' },
    };
    return map[status] || { label: status || 'Unknown', color: 'default' };
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : 'N/A';

  const getSeverityColor = (sev) => {
    const s = (sev || '').toLowerCase();
    if (s === 'error') return 'error';
    if (s === 'warn' || s === 'warning') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading Railway monitoring data...</Typography>
      </Paper>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button size="small" onClick={fetchData} sx={{ ml: 2 }}>Retry</Button>
      </Alert>
    );
  }
  if (!data?.configured) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {data?.message || 'Railway monitoring is not configured.'}
      </Alert>
    );
  }

  const { services = [], databases = {} } = data.data || {};

  return (
    <>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudIcon color="primary" /> Railway Server Monitoring
        </Typography>
        <Tooltip title="Refresh now">
          <IconButton onClick={fetchData} size="small"><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Service Status Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {services.map((svc) => {
          const statusInfo = getStatusLabel(svc.status);
          const logs = serviceLogs[svc.serviceId];
          return (
            <Grid item xs={12} md={4} key={svc.serviceId}>
              <Paper sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getStatusIcon(svc.status)}
                    <Box>
                      <Typography variant="h6">{svc.label || svc.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{svc.dbType && svc.dbType !== 'N/A' ? `${svc.dbType} Backend` : 'Static Site'}</Typography>
                    </Box>
                  </Box>
                  <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                </Box>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Uptime:</strong> {svc.status === 'RUNNING' ? `Running for ${svc.uptime}` : svc.uptime}
                  </Typography>
                  {svc.latestDeployment && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Last Deploy:</strong> {formatDate(svc.latestDeployment.createdAt)}
                    </Typography>
                  )}
                  {svc.error && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{svc.error}</Alert>}
                </Box>
                <Box display="flex" gap={1} mb={1}>
                  <Button size="small" variant="outlined" color="warning"
                    startIcon={actionLoading[`restart-${svc.serviceId}`] ? <CircularProgress size={16} /> : <RestartIcon />}
                    onClick={() => handleAction('restart', svc.serviceId, svc.label || svc.name)}
                    disabled={!!actionLoading[`restart-${svc.serviceId}`]}>
                    Restart
                  </Button>
                  <Button size="small" variant="outlined" color="primary"
                    startIcon={actionLoading[`redeploy-${svc.serviceId}`] ? <CircularProgress size={16} /> : <RedeployIcon />}
                    onClick={() => handleAction('redeploy', svc.serviceId, svc.label || svc.name)}
                    disabled={!!actionLoading[`redeploy-${svc.serviceId}`]}>
                    Redeploy
                  </Button>
                  <Button size="small" variant="text"
                    endIcon={expandedLogs[svc.serviceId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => handleFetchLogs(svc.serviceId)}>
                    Logs
                  </Button>
                </Box>
                {/* Expandable Logs */}
                <Collapse in={!!expandedLogs[svc.serviceId]}>
                  {logs && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 1 }} />
                      <Box display="flex" gap={2} mb={1}>
                        <Chip icon={<ErrorIcon />} label={`${logs.errorCount || 0} errors (24h)`}
                          color={logs.errorCount > 0 ? 'error' : 'default'} size="small" />
                        <Chip icon={<WarningIcon />} label={`${logs.warningCount || 0} warnings (24h)`}
                          color={logs.warningCount > 0 ? 'warning' : 'default'} size="small" />
                      </Box>
                      {logs.logs?.length > 0 ? (
                        <TableContainer sx={{ maxHeight: 300, overflowX: 'auto' }}>
                          <Table size="small" stickyHeader sx={{ minWidth: 500 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 60 }}>Level</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Timestamp</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', minWidth: 250 }}>Message</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {logs.logs.map((log, idx) => (
                                <TableRow key={idx} sx={{
                                  bgcolor: getSeverityColor(log.severity) === 'error' ? 'error.light'
                                    : getSeverityColor(log.severity) === 'warning' ? 'warning.light' : 'inherit',
                                }}>
                                  <TableCell>
                                    <Chip label={log.severity || 'info'} size="small" color={getSeverityColor(log.severity)} />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(log.timestamp)}</TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.message}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No recent logs available.</Typography>
                      )}
                    </Box>
                  )}
                </Collapse>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Database Health Cards */}
      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <DatabaseIcon color="primary" /> Database Health
      </Typography>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* MongoDB */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">MongoDB</Typography>
              <Chip
                label={databases.mongodb?.healthy ? 'Connected' : 'Disconnected'}
                color={databases.mongodb?.healthy ? 'success' : 'error'}
                size="small"
              />
            </Box>
            {databases.mongodb?.responseTime && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Response Time: {databases.mongodb.responseTime}ms
              </Typography>
            )}
            {databases.mongodb?.error && (
              <Alert severity="error" sx={{ mt: 1, py: 0 }}>{databases.mongodb.error}</Alert>
            )}
          </Paper>
        </Grid>
        {/* PostgreSQL */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold">PostgreSQL</Typography>
              <Chip
                label={databases.postgresql?.healthy ? 'Connected' : 'Disconnected'}
                color={databases.postgresql?.healthy ? 'success' : 'error'}
                size="small"
              />
            </Box>
            {databases.postgresql?.responseTime && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Response Time: {databases.postgresql.responseTime}ms
              </Typography>
            )}
            {databases.postgresql?.error && (
              <Alert severity="error" sx={{ mt: 1, py: 0 }}>{databases.postgresql.error}</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, serviceId: null, serviceName: '' })}>
        <DialogTitle>Confirm {confirmDialog.action === 'restart' ? 'Restart' : 'Redeploy'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmDialog.action} <strong>{confirmDialog.serviceName}</strong>?
            {confirmDialog.action === 'redeploy' && ' This will trigger a full redeployment which may cause brief downtime.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: null, serviceId: null, serviceName: '' })}>Cancel</Button>
          <Button onClick={handleConfirmAction} color={confirmDialog.action === 'restart' ? 'warning' : 'primary'} variant="contained">
            {confirmDialog.action === 'restart' ? 'Restart' : 'Redeploy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default RailwayMonitoringPanel;

